import { NayaxCapturePolicy, NayaxCaptureStatus, NayaxPaymentState, Order } from '../api/api_pb';
import { Timestamp } from '@bufbuild/protobuf/wkt';

/**
 * Detects Nayax orders by checking either the backend flag or the presence of
 * preauth payment data. The backend doesn't always set isNayaxOrder for sale
 * orders even when they go through a Nayax terminal.
 */
export function isEffectivelyNayaxOrder(order: Pick<Order, 'isNayaxOrder' | 'paymentInformation'>): boolean {
    if (order.isNayaxOrder) return true;
    const pi = order.paymentInformation;
    return !!pi && !!pi.preauthAmountCents && Number(pi.preauthAmountCents) > 0;
}

export function isRefundable(order: Order): boolean {
    if (order.refundStatus?.refunded) return false;
    // All Nayax orders are handled through capture/void flow, not standard refund
    if (isEffectivelyNayaxOrder(order)) return false;
    return !!order.paymentInformation?.rrn;
}

export function formatCapturePolicy(policy: NayaxCapturePolicy): string {
    switch (policy) {
        case NayaxCapturePolicy.IMMEDIATE_ON_DISPENSE:
            return 'Immediate on Dispense';
        case NayaxCapturePolicy.IMMEDIATE_ON_RETURN:
            return 'Immediate on Return';
        case NayaxCapturePolicy.MANUAL:
            return 'Manual';
        case NayaxCapturePolicy.AUTO_ON_RETURN_AFTER_HOURS:
            return 'Auto After Return';
        default:
            return 'Immediate on Dispense';
    }
}

export function formatCaptureStatus(status: NayaxCaptureStatus): string {
    switch (status) {
        case NayaxCaptureStatus.PENDING:
            return 'Pending';
        case NayaxCaptureStatus.CAPTURED:
            return 'Captured';
        case NayaxCaptureStatus.VOIDED:
            return 'Voided';
        case NayaxCaptureStatus.FAILED:
            return 'Failed';
        default:
            return '';
    }
}

export function getCaptureStatusColor(status: NayaxCaptureStatus): string {
    switch (status) {
        case NayaxCaptureStatus.PENDING:
            return 'bg-yellow-100 text-yellow-800';
        case NayaxCaptureStatus.CAPTURED:
            return 'bg-green-100 text-green-800';
        case NayaxCaptureStatus.VOIDED:
            return 'bg-gray-100 text-gray-800';
        case NayaxCaptureStatus.FAILED:
            return 'bg-red-100 text-red-800';
        default:
            return '';
    }
}

export function formatNayaxPaymentState(state: NayaxPaymentState): string {
    switch (state) {
        case NayaxPaymentState.AUTHORIZED:
            return 'Authorized';
        case NayaxPaymentState.CAPTURING:
            return 'Capturing';
        case NayaxPaymentState.VOIDING:
            return 'Voiding';
        case NayaxPaymentState.CAPTURED:
            return 'Captured';
        case NayaxPaymentState.VOIDED:
            return 'Voided';
        case NayaxPaymentState.CAPTURE_FAILURE:
            return 'Capture Failed';
        case NayaxPaymentState.VOID_FAILURE:
            return 'Void Failed';
        case NayaxPaymentState.EXPIRED_FAILURE:
            return 'Expired';
        case NayaxPaymentState.ERROR:
            return 'Error';
        default:
            return '';
    }
}

export function getNayaxPaymentStateColor(state: NayaxPaymentState): string {
    switch (state) {
        case NayaxPaymentState.AUTHORIZED:
            return 'bg-yellow-100 text-yellow-800';
        case NayaxPaymentState.CAPTURING:
        case NayaxPaymentState.VOIDING:
            return 'bg-blue-100 text-blue-800';
        case NayaxPaymentState.CAPTURED:
            return 'bg-green-100 text-green-800';
        case NayaxPaymentState.VOIDED:
            return 'bg-gray-100 text-gray-800';
        case NayaxPaymentState.CAPTURE_FAILURE:
        case NayaxPaymentState.VOID_FAILURE:
        case NayaxPaymentState.EXPIRED_FAILURE:
        case NayaxPaymentState.ERROR:
            return 'bg-red-100 text-red-800';
        default:
            return '';
    }
}

export function hasGranularNayaxPaymentState(order: Pick<Order, 'nayaxPaymentState'>): boolean {
    return order.nayaxPaymentState !== undefined && order.nayaxPaymentState !== NayaxPaymentState.UNSPECIFIED;
}

export function formatOrderCaptureStatus(order: Pick<Order, 'captureStatus' | 'nayaxPaymentState'>): string {
    if (hasGranularNayaxPaymentState(order)) {
        return formatNayaxPaymentState(order.nayaxPaymentState);
    }
    return formatCaptureStatus(order.captureStatus);
}

export function getOrderCaptureStatusColor(order: Pick<Order, 'captureStatus' | 'nayaxPaymentState'>): string {
    if (hasGranularNayaxPaymentState(order)) {
        return getNayaxPaymentStateColor(order.nayaxPaymentState);
    }
    return getCaptureStatusColor(order.captureStatus);
}

export function timestampToEpochSeconds(ts?: Timestamp): number {
    if (!ts) return 0;
    return Number(ts.seconds);
}

export function formatCountdown(targetEpochSeconds: number): string {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const diffSeconds = targetEpochSeconds - nowSeconds;

    if (diffSeconds <= 0) return 'Expired';

    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    if (minutes === 0) return '<1m';
    return `${minutes}m`;
}

export function isOrderPendingCapture(order: Pick<Order, 'captureStatus' | 'nayaxPaymentState'>): boolean {
    // New model: only AUTHORIZED is actionable for manual capture/void.
    if (hasGranularNayaxPaymentState(order)) {
        return order.nayaxPaymentState === NayaxPaymentState.AUTHORIZED;
    }
    // Legacy fallback: PENDING means actionable.
    return order.captureStatus === NayaxCaptureStatus.PENDING;
}

export function isOrderCaptured(order: Pick<Order, 'captureStatus' | 'nayaxPaymentState'>): boolean {
    if (hasGranularNayaxPaymentState(order)) {
        return order.nayaxPaymentState === NayaxPaymentState.CAPTURED;
    }
    return order.captureStatus === NayaxCaptureStatus.CAPTURED;
}

export function isOrderFailed(order: Pick<Order, 'captureStatus' | 'nayaxPaymentState'>): boolean {
    if (hasGranularNayaxPaymentState(order)) {
        return [
            NayaxPaymentState.CAPTURE_FAILURE,
            NayaxPaymentState.VOID_FAILURE,
            NayaxPaymentState.EXPIRED_FAILURE,
            NayaxPaymentState.ERROR,
        ].includes(order.nayaxPaymentState);
    }
    return order.captureStatus === NayaxCaptureStatus.FAILED;
}

export function validateCaptureAmount(value: string, maxMajorUnits?: number): { valid: boolean; error?: string } {
    void maxMajorUnits;
    if (value === '') return { valid: false, error: 'Amount is required' };
    const regex = /^[0-9]+(\.[0-9]{1,2})?$/;
    if (!regex.test(value)) return { valid: false, error: 'Enter a valid amount (e.g. 12.50)' };
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return { valid: false, error: 'Amount must be non-negative' };
    return { valid: true };
}

export function bigintToTimestamp(epochSeconds: bigint): Timestamp | undefined {
    if (!epochSeconds || epochSeconds === BigInt(0)) return undefined;
    return { seconds: epochSeconds, nanos: 0, $typeName: 'google.protobuf.Timestamp' } as Timestamp;
}
